/**
 * Content Validation Utility
 * Validates section data before Firebase updates
 */

// Basic type validation helpers
const validators = {
  isString: (value) => typeof value === "string",
  isNumber: (value) => typeof value === "number",
  isBoolean: (value) => typeof value === "boolean",
  isArray: (value) => Array.isArray(value),
  isObject: (value) =>
    value !== null && typeof value === "object" && !Array.isArray(value),
  isValidDate: (value) => !isNaN(Date.parse(value)),
  isValidUrl: (value) => {
    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  },
};

// Common field validators
const commonFieldValidators = {
  id: (value) => validators.isString(value) && value.length > 0,
  title: (value) => validators.isString(value) && value.length > 0,
  displayCount: (value) => validators.isNumber(value) && value > 0,
  lastUpdated: (value) => validators.isValidDate(value),
  currentContent: (value) => !value || validators.isString(value),
};

// Section-specific validators
const sectionValidators = {
  hero: (section) => {
    const errors = [];

    if (!section.subtitle || !validators.isString(section.subtitle)) {
      errors.push("Hero section requires a valid subtitle string");
    }

    if (!section.ctaButton || !validators.isObject(section.ctaButton)) {
      errors.push("Hero section requires a ctaButton object");
    } else {
      if (!validators.isString(section.ctaButton.text)) {
        errors.push("ctaButton requires a valid text string");
      }
      if (!validators.isString(section.ctaButton.link)) {
        errors.push("ctaButton requires a valid link string");
      }
    }

    if (!validators.isArray(section.bookIds)) {
      errors.push("bookIds must be an array");
    }

    return { isValid: errors.length === 0, errors };
  },

  features: (section) => {
    const errors = [];

    if (!section.features || !validators.isArray(section.features)) {
      errors.push("Features section requires a features array");
    } else {
      section.features.forEach((feature, index) => {
        if (!feature.id || !validators.isNumber(feature.id)) {
          errors.push(`Feature ${index + 1} requires a valid numeric id`);
        }
        if (!feature.title || !validators.isString(feature.title)) {
          errors.push(`Feature ${index + 1} requires a valid title string`);
        }
        if (!feature.description || !validators.isString(feature.description)) {
          errors.push(
            `Feature ${index + 1} requires a valid description string`
          );
        }
        if (!feature.icon || !validators.isString(feature.icon)) {
          errors.push(`Feature ${index + 1} requires a valid icon string`);
        }
      });
    }

    return { isValid: errors.length === 0, errors };
  },

  trending: (section) => {
    const errors = [];

    if (!validators.isArray(section.trendingBooks)) {
      errors.push("trendingBooks must be an array");
    }

    if (!section.bookDisplay || !validators.isObject(section.bookDisplay)) {
      errors.push("bookDisplay options are required");
    } else {
      const requiredOptions = [
        "showRating",
        "showReviewCount",
        "showPosition",
        "showQuickView",
      ];
      requiredOptions.forEach((option) => {
        if (typeof section.bookDisplay[option] !== "boolean") {
          errors.push(`bookDisplay.${option} must be a boolean`);
        }
      });
    }

    return { isValid: errors.length === 0, errors };
  },
};

// Main validation function
export function validateSection(section) {
  // First validate common fields
  const errors = [];

  // Check required common fields
  Object.entries(commonFieldValidators).forEach(([field, validator]) => {
    if (section[field] && !validator(section[field])) {
      errors.push(`Invalid ${field} field`);
    }
  });

  // If there are errors in common fields, return early
  if (errors.length > 0) {
    return { isValid: false, errors };
  }

  // Validate section-specific fields
  if (sectionValidators[section.id]) {
    const { isValid, errors: sectionErrors } =
      sectionValidators[section.id](section);
    return { isValid, errors: sectionErrors };
  }

  // If no specific validator exists for this section type
  return {
    isValid: true,
    warnings: [`No specific validation rules for section type: ${section.id}`],
  };
}

// Validate entire content object
export function validateContent(content) {
  if (!validators.isObject(content)) {
    return { isValid: false, errors: ["Content must be an object"] };
  }

  const errors = [];
  const warnings = [];

  // Validate sections array
  if (!validators.isArray(content.sections)) {
    errors.push("Content must have a sections array");
    return { isValid: false, errors };
  }

  // Validate each section
  content.sections.forEach((section, index) => {
    const {
      isValid,
      errors: sectionErrors,
      warnings: sectionWarnings,
    } = validateSection(section);

    if (!isValid) {
      errors.push(`Section ${index} (${section.id}) validation failed:`);
      errors.push(...sectionErrors.map((err) => `  - ${err}`));
    }

    if (sectionWarnings) {
      warnings.push(`Section ${index} (${section.id}) warnings:`);
      warnings.push(...sectionWarnings.map((warn) => `  - ${warn}`));
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}
